import { Component, OnInit, ViewEncapsulation, ChangeDetectionStrategy, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Subject, takeUntil } from 'rxjs';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { MemberInfo, SelectorOrganization, UserData } from '@tgx/shared/interfaces';
import { ManagePermissionsService } from '@tgx/shared/permissions';
import { PlatformInfoService } from '@tgx/shared/services';
import { ToastsService } from '@tgx/shared/toasts';
import { PartnerType, WebComponent, WebFeature } from '@tgx/shared/enums';
import { DOCUMENTATION_LINKS } from '@tgx/shared/constants';
import { buildPartnerStatusProgress, calculateDynamicProgress } from '../../utils/build-partner-status-progress';
import { ONBOARDING_STEPS } from '../../utils/onboarding-steps';
import { FileStatus } from '../../enums/file-status.enum';
import { OnboardingImplementationService } from '../../services/onboarding-implementation.service';
import { OnboardingProgessService } from '../../services/onboarding-progess.service';
import { OnboardingValidationModalComponent } from '../onboarding-validation-modal/onboarding-validation-modal.component';
import { UserInviteModalComponent, UserManagementService } from '@tgx/shared/user-management';

@Component({
  selector: 'tgx-onboarding-implementation-home',
  templateUrl: './onboarding-implementation-home.component.html',
  styleUrls: ['./onboarding-implementation-home.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class OnboardingImplementationHomeComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private modalService = inject(NgbModal);
  private toastService = inject(ToastsService);
  private platformInfoService = inject(PlatformInfoService);
  private managePermissionsService = inject(ManagePermissionsService);
  private onboardingProcessService = inject(OnboardingProgessService);
  private onboardingService = inject(OnboardingImplementationService);
  private settingsMembersService = inject(UserManagementService);

  protected readonly documentationLinks = DOCUMENTATION_LINKS;
  // User and organization data
  private organization: SelectorOrganization;
  private orgUsersList: string[] = [];
  private membersRequestId = 0;
  protected isLoadingMembers = true;
  protected membersLoadFailed = false;
  protected canInviteUsers = false;
  protected canEditRoles = false;
  protected canEditContacts = false;
  protected inviteUsersTooltipText = '';
  protected editRolesTooltipText = '';
  protected editContactsTooltipText = '';
  protected loggedUser: UserData;
  protected companyName: string;
  protected userImg: string;

  // Onboarding state and steps
  protected steps = ONBOARDING_STEPS;
  protected actualStep = -1;
  protected progress = 0;
  private readonly progressIncrement = 12.5;

  // Subscriptions and timers
  private destroy$ = new Subject<void>();
  private certificationIntervalId: any;

  // Constants and flags
  protected readonly FileStatus = FileStatus;

  async ngOnInit() {
    this.platformInfoService.organizationSelected$.pipe(takeUntil(this.destroy$)).subscribe(async (org) => {
      if (!org) return;

      this.organization = org as SelectorOrganization;
      this.orgUsersList = [];
      this.isLoadingMembers = true;
      this.membersLoadFailed = false;
      const requestId = ++this.membersRequestId;
      const loaded = await this.settingsMembersService.retrieveMembersByOrg(org.code);
      if (requestId !== this.membersRequestId || this.organization?.code !== org.code) return;

      this.membersLoadFailed = !loaded || this.settingsMembersService.currentMembersOrgCode !== org.code;
      this.isLoadingMembers = false;
    });

    this.settingsMembersService.allMembers.pipe(takeUntil(this.destroy$)).subscribe((members) => {
      if (this.settingsMembersService.currentMembersOrgCode !== this.organization?.code) return;
      this.orgUsersList = members?.map((member) => member.email) ?? [];
    });

    this.managePermissionsService.ready$.pipe(takeUntil(this.destroy$)).subscribe((ready) => {
      if (!ready) {
        this.canInviteUsers = false;
        return;
      }

      const inviteVisibility = this.managePermissionsService.checkVisibility(
        WebComponent.SETTINGS,
        WebFeature.INVITE_USERS,
      );
      const rolesVisibility = this.managePermissionsService.checkVisibility(
        WebComponent.SETTINGS,
        WebFeature.UPGRADE_PERMISSIONS,
      );
      const contactsVisibility = this.managePermissionsService.checkVisibility(
        WebComponent.SETTINGS,
        WebFeature.COMPANY_CONTACTS,
      );

      this.canInviteUsers = inviteVisibility.isVisible;
      this.inviteUsersTooltipText = this.managePermissionsService.buildVisibilityMessage(inviteVisibility);
      this.canEditRoles = rolesVisibility.isVisible;
      this.editRolesTooltipText = this.managePermissionsService.buildVisibilityMessage(rolesVisibility);
      this.canEditContacts = contactsVisibility.isVisible;
      this.editContactsTooltipText = this.managePermissionsService.buildVisibilityMessage(contactsVisibility);
    });

    combineLatest([this.platformInfoService.userLogged$, this.platformInfoService.organizationSelected$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(async ([userData, selectedOrg]) => {
        if (userData) {
          this.loggedUser = userData;
          this.userImg = userData.picture;
        }

        if (selectedOrg) {
          this.organization = selectedOrg;
          this.companyName = selectedOrg.label;

          this.onboardingProcessService.setStartDate(this.organization.code, PartnerType.BUYER);
        }
      });

    await this.checkCertificationStatus();
    this.buildPartnerStatusProgress();
  }

  ngOnDestroy() {
    // Clear the interval when the component is destroyed
    if (this.certificationIntervalId) {
      clearInterval(this.certificationIntervalId);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  private async checkCertificationStatus() {
    let stillInProgress = false;
    const certificationData = await this.onboardingService.checkCertificationStatus(this.organization.code);

    const validationMap = new Map<string, { status: string; error: any }>(
      certificationData?.validations?.map((v) => [v.scenario, { status: v.status, error: v.error }]),
    );
    // update steps status from api response
    this.steps.forEach((step) => {
      step.sections?.forEach((section) => {
        section.stepsFlow.forEach((flow) => {
          flow.files.forEach((file) => {
            const validation = validationMap.get(file.scenario);
            if (validation) {
              file.status = validation.status as FileStatus;
              file.error = validation.error;
              if (file.status === FileStatus.IN_PROGRESS) stillInProgress = true;
            }
          });
        });
        section.hasErrors = section.stepsFlow?.some((flow) => flow.files?.some((file) => file.error !== null));
        this.getTotalFilesOK(section);
      });
    });
    // Launch the cron every 3 seconds if still validating files
    if (stillInProgress) {
      setTimeout(() => this.checkCertificationStatus(), 3000);
    } else {
      this.progress = await calculateDynamicProgress(this.steps, this.progressIncrement, this.actualStep);
    }
  }

  private buildPartnerStatusProgress() {
    // Use the utility function to get updated steps, actualStep, and progress
    const result = buildPartnerStatusProgress(this.steps, this.progressIncrement);
    this.steps = result.steps;
    this.actualStep = result.actualStep;
    this.progress = result.progress;
  }

  protected nextStep(skipValidation = false) {
    if (skipValidation || this.validateStep()) {
      this.updateStep(this.actualStep, { success: true });
      this.updateStep(this.actualStep, { active: false });
      this.actualStep++;
      this.updateStep(this.actualStep, { active: true });
      this.progress += this.progressIncrement * 2;
    }
  }

  protected validateStep(): boolean {
    const isValid =
      this.actualStep === -1 ||
      this.steps[this.actualStep].sections?.every((section) =>
        section.stepsFlow.every((flow) => flow.files.every((file) => file.status === FileStatus.OK)),
      );
    return isValid;
  }

  private updateStep(index: number, changes: Partial<{ active: boolean; success: boolean }>) {
    this.steps = this.steps.map((step, i) => (i === index ? { ...step, ...changes } : step));
  }

  protected async onFileSelected(event: any, fileInput: any) {
    fileInput.status = FileStatus.IN_PROGRESS;
    fileInput.error = null;

    const file = event.target.files[0];
    if (!file || (file.type !== 'application/json' && !file.name.endsWith('.json'))) {
      fileInput.status = FileStatus.ERROR;
      fileInput.error = 'Only JSON files are allowed';
      return;
    }

    const newName = fileInput.file;
    const fileWithNewName = this.changeFileName(file, newName);
    this.uploadFile(fileWithNewName);
    event.target.value = '';
  }

  private async uploadFile(file: File) {
    try {
      const response = await this.onboardingService.uploadFile(file, this.organization.code);
      if (response.success) {
        await this.onboardingService.writeCertificationStatus(this.organization.code, file.name);
        this.checkCertificationStatus();
      }
    } catch (error) {
      this.toastService.error('Error uploading file: ' + error);
    }
  }

  private changeFileName(file: File, newName: string): File {
    const newFile = new File([file], newName, { type: file.type });
    return newFile;
  }

  private getTotalFilesOK(section: any) {
    section.totalFilesOk = section.stepsFlow.reduce(
      (count: number, flow: any) => count + flow.files.filter((file: any) => file.status === FileStatus.OK).length,
      0,
    );
  }

  protected openOnboardingValidationModal() {
    const modal = this.modalService.open(OnboardingValidationModalComponent, { size: 'lg', windowClass: 'xen-modal' });
    modal.result.then(
      (skip: boolean) => {
        if (skip) {
          this.nextStep(true);
        }
      },
      () => {
        // Modal dismissed by user: no action needed.
      },
    );
  }

  protected async finishOnboarding() {
    try {
      await this.onboardingProcessService.setBuyerEndDate(this.organization.code);
    } catch (error) {
      this.toastService.handleGatewayAndGraphqlErrors(error);
      return;
    }
    await this.onboardingService.sendEndNotification(this.organization.label, this.organization.code);
    this.router.navigate(['/dashboard']);
  }

  protected async inviteUser() {
    if (this.membersLoadFailed) {
      this.toastService.error('Users could not be loaded. Try again before inviting a user.');
      return;
    }

    if (this.isLoadingMembers) {
      return;
    }

    if (!this.canInviteUsers) {
      this.toastService.error(this.inviteUsersTooltipText || 'You do not have permission to invite users.');
      return;
    }

    if (!this.organization?.code) {
      this.toastService.error('Select an organization before inviting a user.');
      return;
    }

    const modal = this.modalService.open(UserInviteModalComponent, {
      size: 'xl',
      windowClass: 'xen-modal',
      backdrop: 'static',
      keyboard: false,
    });
    const organizationCode = this.organization.code;

    modal.componentInstance.orgUsersList = this.orgUsersList;
    modal.componentInstance.canEditRoles = this.canEditRoles;
    modal.componentInstance.editRolesTooltipText = this.editRolesTooltipText;
    modal.componentInstance.canEditContacts = this.canEditContacts;
    modal.componentInstance.editContactsTooltipText = this.editContactsTooltipText;

    modal.result.then(
      (newUser: MemberInfo) => {
        if (!newUser || this.organization?.code !== organizationCode) return;

        const currentMembers = this.settingsMembersService.allMembers.value ?? [];
        if (currentMembers.some((member) => member.email === newUser.email)) return;

        const updatedMembers = [...currentMembers, newUser];
        this.orgUsersList = updatedMembers.map((member) => member.email);
        this.settingsMembersService.allMembers.next(updatedMembers);
      },
      () => {
        // Modal dismissed by user: no user invited.
      },
    );
  }

  protected async retryMembersLoading() {
    if (!this.organization?.code) return;

    this.isLoadingMembers = true;
    this.membersLoadFailed = false;
    this.orgUsersList = [];
    const requestId = ++this.membersRequestId;
    const loaded = await this.settingsMembersService.retrieveMembersByOrg(this.organization.code);
    if (requestId !== this.membersRequestId) return;

    this.membersLoadFailed = !loaded || this.settingsMembersService.currentMembersOrgCode !== this.organization.code;
    this.isLoadingMembers = false;
  }
}
